import { getGlobalToast } from '@/components/layout/toastUtils'
import { refreshAuth } from '@/services/api/authRefresh'
import { useAuthStore } from '@/store/authStore'

// Skew (ms) applied to the JWT `exp` claim so we treat the token as expired
// slightly early and avoid narrowly-valid requests racing the server clock.
const EXPIRY_SKEW_MS = 30_000

function base64UrlDecode(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')
  return atob(padded)
}

function isTokenExpired(token: string, now: number = Date.now()): boolean {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return true
    const payload = JSON.parse(base64UrlDecode(parts[1]))
    if (typeof payload.exp !== 'number') return true
    return payload.exp * 1000 - EXPIRY_SKEW_MS < now
  } catch {
    return true
  }
}

// Called on app mount, on tab focus and on reconnect. If the stored JWT has
// expired, refresh it through the client's shared refresh (one in-flight
// refresh per tab, serialized across tabs). Only an explicit rejection of the
// refresh token ends the session (logout + toast; PrivateRoute then redirects
// to /login). Offline / 5xx / timeout keep the expired session — and with it
// the offline queue — until the next online or visibility event retries.
export async function validateStoredAuth(): Promise<void> {
  const state = useAuthStore.getState()
  if (!state.token) return
  if (!isTokenExpired(state.token)) return

  if ((await refreshAuth()) !== 'rejected') return
  // Signed out meanwhile (by the user, or by another tab) — that path already
  // cleaned up and needs no "session expired" toast.
  if (!useAuthStore.getState().token) return

  useAuthStore.getState().logout()
  const toast = getGlobalToast()
  if (toast) toast('Your session expired. Please log in again.', 'error')
}

function hasToken(persisted: string | null): boolean {
  if (!persisted) return false
  try {
    return Boolean(JSON.parse(persisted)?.state?.token)
  } catch {
    return false
  }
}

/**
 * Keeps this tab's auth in step with the others. `persist` writes every auth
 * change to localStorage, which fires a `storage` event in every OTHER tab:
 * - a logout elsewhere logs this tab out too, through `logout()`, so its
 *   in-memory query cache can't re-persist the previous session's data;
 * - anything else (a token rotation, a login) is pulled in via `rehydrate()`,
 *   so this tab never presents a refresh token another tab already spent.
 * Returns the unsubscribe function.
 */
export function subscribeToCrossTabAuth(): () => void {
  const storageKey = useAuthStore.persist.getOptions().name
  const onStorage = (event: StorageEvent) => {
    if (event.key !== storageKey) return
    if (hasToken(event.oldValue) && !hasToken(event.newValue)) {
      useAuthStore.getState().logout()
      return
    }
    void useAuthStore.persist.rehydrate()
  }
  window.addEventListener('storage', onStorage)
  return () => window.removeEventListener('storage', onStorage)
}
